import React, { createContext, useContext, useState, useEffect } from 'react';

import { ThemeMode, defaultTheme } from './theme.js';

interface ThemeContextType {
  theme: ThemeMode;
  toggleTheme: () => void;
  setTheme: (theme: ThemeMode) => void;
}

// Create context with default values
const ThemeContext = createContext<ThemeContextType>({
  theme: defaultTheme,
  toggleTheme: () => {},
  setTheme: () => {},
});

// Local storage key for theme
const THEME_STORAGE_KEY = 'app-theme-preference';

interface ThemeProviderProps {
  children: React.ReactNode;
  initialTheme?: ThemeMode;
}

/**
 * Theme provider component that manages theme state
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  initialTheme,
}) => {
  // Initialize theme from props, local storage, or system preference
  const [theme, setThemeState] = useState<ThemeMode>(() => {
    // Check if theme is provided as prop
    if (initialTheme) {
      return initialTheme;
    }
    
    // Check if theme is stored in local storage
    const storedTheme = typeof window !== 'undefined' 
      ? localStorage.getItem(THEME_STORAGE_KEY) as ThemeMode | null
      : null;
      
    if (storedTheme === 'light' || storedTheme === 'dark') {
      return storedTheme;
    }
    
    // Check system preference
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
    }
    
    // Default theme
    return defaultTheme;
  });
  
  // Update local storage and cookie when theme changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Update local storage
      localStorage.setItem(THEME_STORAGE_KEY, theme);

      // Update cookie for SSR
      // Set cookie path to '/' and maxAge for persistence (e.g., 1 year)
      document.cookie = `${THEME_STORAGE_KEY}=${theme}; path=/; max-age=31536000; SameSite=Lax`;
      
      // Update document class for global CSS, avoiding unnecessary changes during hydration
      const root = document.documentElement;
      if (theme === 'dark') {
        if (!root.classList.contains('dark')) {
          root.classList.add('dark');
        }
      } else {
        if (root.classList.contains('dark')) {
          root.classList.remove('dark');
        }
      }
    }
  }, [theme]);
  
  // Toggle between light and dark themes
  const toggleTheme = () => {
    setThemeState(prevTheme => (prevTheme === 'light' ? 'dark' : 'light'));
  };
  
  // Set theme directly
  const setTheme = (newTheme: ThemeMode) => {
    setThemeState(newTheme);
  };
  
  // Context value
  const contextValue: ThemeContextType = {
    theme,
    toggleTheme,
    setTheme,
  };
  
  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Hook to use theme context
 */
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
};

export default ThemeProvider;
